import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Ban, CheckCircle2, Eye, ShieldOff, XCircle } from "lucide-react";

import { DashboardShell } from "@/components/DashboardShell";
import { ADMIN_NAV } from "@/lib/nav";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/lib/auth";
import {
  REPORT_REASON_LABEL,
  REPORT_STATUSES,
  REPORT_STATUS_CLASS,
  REPORT_STATUS_LABEL,
  listAllReports,
  suspendListing,
  suspendPartner,
  updateReport,
  type AbuseReport,
  type ReportStatus,
} from "@/lib/abuse-reports";

type Tab = "all" | ReportStatus;

export const Route = createFileRoute("/_authenticated/admin/user-reports")({
  head: () => ({
    meta: [
      { title: "Reports · Landoo Admin" },
      {
        name: "description",
        content:
          "Review reports raised by Landoo users against listings and partners, and take moderation action.",
      },
      { property: "og:title", content: "Reports · Landoo Admin" },
      {
        property: "og:description",
        content:
          "Review reports raised by Landoo users against listings and partners, and take moderation action.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminUserReportsPage,
});

function AdminUserReportsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin", "abuse-reports"],
    queryFn: listAllReports,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "abuse-reports"] });

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (tab !== "all" && r.status !== tab) return false;
      if (!q) return true;
      return `${r.listing_title ?? ""} ${r.partner_name ?? ""} ${r.reporter_name ?? ""} ${r.comment ?? ""}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, tab, search]);

  const act = useMutation({
    mutationFn: async (input: {
      report: AbuseReport;
      status?: ReportStatus;
      action?: "suspend_listing" | "suspend_partner";
    }) => {
      const { report, status, action } = input;
      let actionTaken: string | undefined;
      if (action === "suspend_listing" && report.service_type && report.listing_id) {
        await suspendListing(report.service_type, report.listing_id);
        actionTaken = "Listing suspended";
      }
      if (action === "suspend_partner" && report.partner_id) {
        await suspendPartner(report.partner_id);
        actionTaken = "Partner suspended";
      }
      await updateReport(report.id, {
        status: status ?? (action ? "resolved" : undefined),
        adminNotes: notes.trim() ? notes.trim() : undefined,
        actionTaken,
        reviewerId: user?.id ?? null,
      });
    },
    onSuccess: () => {
      toast.success("Report updated. The reporter has been notified.");
      setNotes("");
      setOpenId(null);
      refresh();
    },
    onError: () => toast.error("Could not update this report."),
  });

  const counts = (s: Tab) =>
    s === "all" ? rows.length : rows.filter((r) => r.status === s).length;

  return (
    <DashboardShell title="Reports" role="Admin" items={ADMIN_NAV}>
      <div className="grid gap-3 sm:grid-cols-4">
        {REPORT_STATUSES.map((s) => (
          <div key={s} className="rounded-xl border border-border bg-card p-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {REPORT_STATUS_LABEL[s]}
            </div>
            <div className="text-lg font-bold text-brown-dark">{counts(s)}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {(["all", ...REPORT_STATUSES] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                tab === t ? "bg-brown-dark text-white" : "bg-secondary text-brown-dark"
              }`}
            >
              {t === "all" ? "All" : REPORT_STATUS_LABEL[t]} ({counts(t)})
            </button>
          ))}
        </div>
        <Input
          className="sm:w-64"
          placeholder="Search listing, partner, reporter"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading reports…</p>
      ) : list.length === 0 ? (
        <p className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No reports in this view.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {list.map((r) => {
            const expanded = openId === r.id;
            return (
              <li key={r.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${REPORT_STATUS_CLASS[r.status]}`}
                      >
                        {REPORT_STATUS_LABEL[r.status]}
                      </span>
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold capitalize text-brown-dark">
                        {r.target_type}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-sm font-semibold text-brown-dark">
                      {r.listing_title ?? r.partner_name ?? "Reported item"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {REPORT_REASON_LABEL[r.reason]} · reported by {r.reporter_name ?? "a user"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenId(expanded ? null : r.id);
                      setNotes(r.admin_notes ?? "");
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
                  >
                    <Eye className="h-4 w-4" /> {expanded ? "Hide" : "View"}
                  </button>
                </div>

                {expanded && (
                  <div className="mt-3 space-y-3 border-t border-border pt-3">
                    {r.comment && <p className="text-sm text-brown-dark">“{r.comment}”</p>}
                    {r.action_taken && (
                      <p className="text-xs font-semibold text-brown-dark">
                        Action taken: {r.action_taken}
                      </p>
                    )}
                    <textarea
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Admin notes (shared with the reporter)"
                      className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                    />
                    <div className="flex flex-wrap gap-2">
                      {r.status === "new" && (
                        <ActionBtn
                          icon={<Eye className="h-4 w-4" />}
                          label="Mark under review"
                          onClick={() => act.mutate({ report: r, status: "under_review" })}
                          disabled={act.isPending}
                        />
                      )}
                      <ActionBtn
                        icon={<CheckCircle2 className="h-4 w-4" />}
                        label="Close (resolved)"
                        onClick={() => act.mutate({ report: r, status: "resolved" })}
                        disabled={act.isPending}
                      />
                      <ActionBtn
                        icon={<XCircle className="h-4 w-4" />}
                        label="Reject report"
                        onClick={() => act.mutate({ report: r, status: "rejected" })}
                        disabled={act.isPending}
                      />
                      {r.listing_id && r.service_type && (
                        <ActionBtn
                          icon={<Ban className="h-4 w-4" />}
                          label="Suspend listing"
                          tone="danger"
                          onClick={() => act.mutate({ report: r, action: "suspend_listing" })}
                          disabled={act.isPending}
                        />
                      )}
                      {r.partner_id && (
                        <ActionBtn
                          icon={<ShieldOff className="h-4 w-4" />}
                          label="Suspend partner"
                          tone="danger"
                          onClick={() => act.mutate({ report: r, action: "suspend_partner" })}
                          disabled={act.isPending}
                        />
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </DashboardShell>
  );
}

function ActionBtn({
  icon,
  label,
  onClick,
  disabled,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: "danger";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition disabled:opacity-60 ${
        tone === "danger"
          ? "bg-destructive/10 text-destructive hover:bg-destructive/20"
          : "border border-border text-brown-dark hover:bg-secondary"
      }`}
    >
      {icon} {label}
    </button>
  );
}
